import './modal.scss';

import cn from 'classnames';
import React, { isValidElement, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';

import { MM_MAIN_ELEMENT_ID } from 'shared/consts/dom';
import { useTypedDispatch } from 'store';
import { dropAlertState, setAlertState } from 'store/slices/ui';

import { Backdrop } from './Backdrop/Backdrop';

import { CloseIcon } from 'assets/icons';

type IModalComponentProps = {
  children: React.ReactNode;
  title?: React.ReactNode;
  backdropClose?: boolean;
  customHeader?: React.ReactNode;
  customButtons?: React.ReactNode;
  onClose: () => void;
  className?: string;
  backdropClassName?: string;
  modalRefItem?: React.Ref<HTMLDivElement>;
  edited?: boolean;
} & (
  | { title: React.ReactNode; customHeader?: never }
  | { customHeader: React.ReactNode; title?: never }
);

const ModalComponent: React.FC<IModalComponentProps> = ({
  children,
  title,
  backdropClose = true,
  customHeader: CustomHeader,
  customButtons: CustomButtons,
  className,
  backdropClassName,
  modalRefItem,
  onClose,
  edited = false,
}) => {
  const dispatch = useTypedDispatch();

  const handleClose = useCallback(() => {
    if (edited) {
      dispatch(
        setAlertState({
          type: 'sure',
          text: 'You have unsaved changes. Confirming this will result in losing all unsaved data.',
          onClose: () => dispatch(dropAlertState()),
          onSubmit: () => {
            dispatch(dropAlertState());
            onClose();
          },
        }),
      );

      return;
    }

    onClose();
  }, [onClose, edited, dispatch]);

  const handleClickBackdrop = useCallback(() => {
    if (backdropClose) {
      handleClose();
    }
  }, [backdropClose, handleClose]);

  useEffect(() => {
    const onCloseModalOnEscapePressed = (event: KeyboardEvent) => {
      event.stopPropagation();
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', onCloseModalOnEscapePressed);

    return () => {
      document.removeEventListener('keydown', onCloseModalOnEscapePressed);
    };
  }, [handleClose]);

  function renderTitle(title: React.ReactNode) {
    if (!title) return null;
    if (isValidElement(title)) return title;
    return <h3>{title}</h3>;
  }

  return (
    <Backdrop onClickBackdrop={handleClickBackdrop} className={backdropClassName}>
      <div
        className={cn('mm-general-modal', { [className ?? '']: className })}
        ref={modalRefItem ?? undefined}
      >
        <div className="mm-general-modal__header">
          {CustomHeader && CustomHeader}
          {renderTitle(title)}
          <div className="mm-general-modal__header__close-button" onClick={handleClose}>
            <CloseIcon />
          </div>
        </div>
        <div className="mm-general-modal__content scrollable">{children}</div>
        {CustomButtons && <div className="mm-general-modal__buttons">{CustomButtons}</div>}
      </div>
    </Backdrop>
  );
};

const ModalPortal: React.FC<IModalComponentProps> = props => {
  return createPortal(
    <ModalComponent {...props} />,
    document.getElementById(MM_MAIN_ELEMENT_ID) as HTMLElement,
  );
};

export { ModalPortal as Modal };
